import type { ReactElement } from 'react';
import type { PendingPage } from '@shared/api';
import type { PendingEntry } from '../state/pendingPages';

/**
 * docs/custom-page.md "What the rep sees" (grid): the layout's wireframe, or
 * a generic skeleton when the layout is unknown. `media_split` has no
 * `mediaSide` on `PendingPage`, so it always draws image-on-the-right.
 */
const LAYOUT_WIREFRAME: Partial<Record<NonNullable<PendingPage['layout']>, string>> = {
  media_full: '/layout-tiles/media-full.svg',
  media_split: '/layout-tiles/media-right.svg',
};

/** Grid and present-mode placeholder card for a page the agent is about to add. */
export function PendingPageCard({ entry }: { entry: PendingEntry }): ReactElement {
  const { pending, status } = entry;
  const wireframe = pending.layout ? LAYOUT_WIREFRAME[pending.layout] : undefined;
  const notAdded = status === 'not_added';
  const classes = [
    'proposal-slide',
    'proposal-slide-pending',
    notAdded ? 'proposal-slide-pending-not-added' : '',
  ].filter(Boolean).join(' ');

  return (
    <article
      id={`proposal-slide-${pending.key}`}
      className={classes}
      role="listitem"
      aria-label={notAdded ? `${pending.title ?? 'New page'} not added` : `${pending.title ?? 'New page'}, building`}
    >
      <div className="proposal-slide__preview">
        {wireframe ? (
          <div className="pending-page-wireframe">
            <img src={wireframe} alt="" aria-hidden="true" />
            {!notAdded ? <span className="pending-page-shimmer" aria-hidden="true" /> : null}
          </div>
        ) : (
          <div className={`build-slide-skeleton${notAdded ? '' : ' build-slide-skeleton-active'}`} aria-hidden="true">
            <span className="build-skeleton-title" />
            <span className="build-skeleton-line build-skeleton-line-short" />
            <span className="build-skeleton-line" />
            <span className="build-skeleton-block" />
          </div>
        )}
      </div>
      <div className="proposal-slide__caption">
        {pending.title ? <span className="proposal-slide__title">{pending.title}</span> : null}
        <span className="proposal-slide__status">{notAdded ? 'Not added' : 'Building…'}</span>
      </div>
    </article>
  );
}
